import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Clock, 
  Save, 
  Timer,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const FIELD_CONFIG = {
  countdown_date: {
    label: 'Target Opening Date',
    placeholder: 'YYYY-MM-DD'
  },
  countdown_label: {
    label: 'Pill Badge Text',
    placeholder: 'e.g. Opening Day Countdown',
    maxLength: 100
  },
  countdown_title: {
    label: 'Countdown Main Title',
    placeholder: 'e.g. School Opening Day',
    maxLength: 100
  },
  countdown_description: {
    label: 'Countdown Description',
    placeholder: 'e.g. We are preparing every detail to welcome students and families with excellence.',
    maxLength: 300,
    isTextarea: true,
    rows: 3
  }
};

export default function CountdownForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [sectionId, setSectionId] = useState(38);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Countdown clock state
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Form fields
  const [formData, setFormData] = useState({
    countdown_label: 'Opening Day Countdown',
    countdown_title: 'School Opening Day',
    countdown_description: 'We are preparing every detail to welcome students and families with excellence.',
    countdown_date: '2026-10-31'
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    countdown_label: false,
    countdown_title: false,
    countdown_description: false,
    countdown_date: false
  });

  // Track original values
  const [originalData, setOriginalData] = useState({
    countdown_label: 'Opening Day Countdown',
    countdown_title: 'School Opening Day',
    countdown_description: 'We are preparing every detail to welcome students and families with excellence.',
    countdown_date: '2026-10-31'
  });

  const loadCountdownSection = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      let section = null;
      let pending_changes = [];

      // 1. Try with-pending by slug
      let response = await fetch(
        `${apiBase}/cms/section/countdown/with-pending?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // 2. Try with-pending by ID
      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/38/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      // 3. Fallback to homepage sections
      if (!response.ok) {
        response = await fetch(
          `${apiBase}/homepage/cms/sections?published=true&_t=${Date.now()}`,
          { credentials: 'omit' }
        );
      }

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.data) {
          section = result.data.section || result.data;
          pending_changes = result.data.pending_changes || [];
        } else if (result.ok && Array.isArray(result.data)) {
          section = result.data.find(s => s.section_type === 'countdown' || s.id === 38);
        } else if (Array.isArray(result)) {
          section = result.find(s => s.section_type === 'countdown' || s.id === 38);
        }
      }

      if (section) {
        let published = section.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }

        let draft = section.content;
        if (typeof draft === 'string') {
          try { draft = JSON.parse(draft); } catch (e) { draft = {}; }
        }

        const source = published?.en || published?.id || draft?.en || draft?.id || published || draft || {};

        let initialDate = source.countdown_date || source.target_date || source.date || '2026-10-31';
        if (typeof initialDate === 'string' && initialDate.includes('T')) {
          initialDate = initialDate.split('T')[0];
        }

        const initialData = {
          countdown_label: source.countdown_label || source.label || 'Opening Day Countdown',
          countdown_title: source.countdown_title || source.title || 'School Opening Day',
          countdown_description: source.countdown_description || source.description || 'We are preparing every detail to welcome students and families with excellence.',
          countdown_date: initialDate
        };

        const pendingMap = {
          countdown_label: false,
          countdown_title: false,
          countdown_description: false,
          countdown_date: false
        };

        // When field change is pending, display proposed/new value
        if (Array.isArray(pending_changes)) {
          pending_changes.forEach(change => {
            if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
              const fid = change.field_id;
              if (fid in pendingMap) {
                pendingMap[fid] = true;

                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }
                if (val !== undefined && val !== null && val !== '') {
                  if (fid === 'countdown_date' && typeof val === 'string' && val.includes('T')) {
                    val = val.split('T')[0];
                  }
                  initialData[fid] = val;
                }
              }
            }
          });
        }

        setFormData(initialData);
        setOriginalData({ ...initialData });
        setPendingFields(pendingMap);
        setSectionId(section.id || 38);

        if (isManualRefresh) {
          toastRef.current?.success('Countdown data refreshed successfully');
        }
      }
    } catch (err) {
      console.warn('[CountdownForm] Error loading countdown section:', err.message);
      if (isManualRefresh) {
        toastRef.current?.error('Failed to refresh: ' + err.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadCountdownSection();
  }, [loadCountdownSection]);

  // Live countdown calculation ticker
  useEffect(() => {
    if (!formData.countdown_date) return;

    const calculateTime = () => {
      try {
        const target = new Date(formData.countdown_date + 'T00:00:00');
        const now = new Date();
        const diff = Math.max(target.getTime() - now.getTime(), 0);

        const totalSec = Math.floor(diff / 1000);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const minutes = Math.floor((totalSec % 3600) / 60);
        const seconds = totalSec % 60;

        setTimeRemaining({ days, hours, minutes, seconds });
      } catch (e) {
        // invalid date fallback
      }
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [formData.countdown_date]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toastRef.current?.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    const config = FIELD_CONFIG[fieldName];
    const fieldDisplayName = config?.label || fieldName.replace(/_/g, ' ');

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldDisplayName,
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toastRef.current?.success('Change submitted for Principal review');
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toastRef.current?.error('This field already has a pending approval');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toastRef.current?.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading countdown section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Timer className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="unified-title">Opening Countdown Section (Homepage)</h1>
            <p className="unified-subtitle">Configure target opening date, badge pill text, main title, and description for the homepage countdown card</p>
          </div>
        </div>
        <div className="unified-header-actions">
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={() => loadCountdownSection(true)}
            disabled={refreshing}
            title="Reload data from server"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      <div className="unified-card">
        {/* 1. Countdown Target Date */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Target Opening Date</label>
            {pendingFields.countdown_date && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="date"
              className="unified-input"
              value={formData.countdown_date}
              onChange={(e) => handleFieldChange('countdown_date', e.target.value)}
              disabled={pendingFields.countdown_date}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('countdown_date')}
              disabled={submitting.countdown_date || pendingFields.countdown_date || formData.countdown_date === originalData.countdown_date}
            >
              <Save size={14} /> {submitting.countdown_date ? 'Saving...' : 'Save'}
            </button>
          </div>

          {/* Live Calculated Countdown Preview */}
          {formData.countdown_date && (
            <div style={{
              marginTop: '14px',
              padding: '12px 16px',
              borderRadius: '12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Timer size={14} color="#6366f1" />
                <span>Live Calculated Countdown Preview</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px', maxWidth: '360px' }}>
                <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: '8px', background: '#ffffff', border: '1px solid #cbd5e1' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1 }}>{String(timeRemaining.days).padStart(2, '0')}</span>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Days</span>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: '8px', background: '#ffffff', border: '1px solid #cbd5e1' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1 }}>{String(timeRemaining.hours).padStart(2, '0')}</span>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Hours</span>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: '8px', background: '#ffffff', border: '1px solid #cbd5e1' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1 }}>{String(timeRemaining.minutes).padStart(2, '0')}</span>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Mins</span>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: '8px', background: '#ffffff', border: '1px solid #cbd5e1' }}>
                  <span style={{ display: 'block', fontSize: '18px', fontWeight: '800', color: '#1e293b', lineHeight: 1.1 }}>{String(timeRemaining.seconds).padStart(2, '0')}</span>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Secs</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. Countdown Badge Label */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Pill Badge Text</label>
            {pendingFields.countdown_label && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength={FIELD_CONFIG.countdown_label.maxLength}
              className="unified-input"
              value={formData.countdown_label}
              onChange={(e) => handleFieldChange('countdown_label', e.target.value)}
              disabled={pendingFields.countdown_label}
              placeholder={FIELD_CONFIG.countdown_label.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('countdown_label')}
              disabled={submitting.countdown_label || pendingFields.countdown_label || formData.countdown_label === originalData.countdown_label}
            >
              <Save size={14} /> {submitting.countdown_label ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* 3. Countdown Title */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Countdown Main Title</label>
            {pendingFields.countdown_title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength={FIELD_CONFIG.countdown_title.maxLength}
              className="unified-input"
              value={formData.countdown_title}
              onChange={(e) => handleFieldChange('countdown_title', e.target.value)}
              disabled={pendingFields.countdown_title}
              placeholder={FIELD_CONFIG.countdown_title.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('countdown_title')}
              disabled={submitting.countdown_title || pendingFields.countdown_title || formData.countdown_title === originalData.countdown_title}
            >
              <Save size={14} /> {submitting.countdown_title ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* 4. Countdown Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Countdown Description</label>
            {pendingFields.countdown_description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <textarea
              maxLength={FIELD_CONFIG.countdown_description.maxLength}
              rows={FIELD_CONFIG.countdown_description.rows}
              className="unified-textarea"
              value={formData.countdown_description}
              onChange={(e) => handleFieldChange('countdown_description', e.target.value)}
              disabled={pendingFields.countdown_description}
              placeholder={FIELD_CONFIG.countdown_description.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('countdown_description')}
              disabled={submitting.countdown_description || pendingFields.countdown_description || formData.countdown_description === originalData.countdown_description}
            >
              <Save size={14} /> {submitting.countdown_description ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
